// Barrel module: loads grimoire.json (separate cacheable asset, kept out of
// the main JS bundle by Vite's assetsInlineLimit) and re-exports the data in
// the same shape callers have always used.
//
// Source of truth: src/lib/data/grimoire.json
// Modify it via the appliers (scripts/apply-*.mjs), never by hand.
// Re-publish the read-only artifacts under tools/data/ with `npm run dump`.

import data from './grimoire.json';
import type {
  Conf, Tier, RegId, CurKey,
  Entry, Chapter, Current, GrimoireData,
} from './grimoire.types';

const G = data as unknown as GrimoireData;

// Re-export types so existing imports `from './grimoire'` keep working.
export type { Conf, Tier, RegId, CurKey, Entry, Chapter, Current };

// ── top-level views ─────────────────────────────────────────────────────────

export const CUR: Record<CurKey, Current> = G.currents;
export const REG: Chapter[] = G.regions;
export const THREAD: Record<CurKey, (string | null)[]> = G.threads;

// ── legend maps ─────────────────────────────────────────────────────────────

const accentByReg: Record<RegId, string> = {
  'reg-1': 'var(--nigredo)',
  'reg-2': 'var(--albedo)',
  'reg-3': 'var(--citrinitas)',
  'reg-4': 'var(--rubedo)',
};

export const STAGE_ACCENT: Record<RegId, string> = accentByReg;
export const ROMAN: Record<RegId, string> = G.legend.roman as Record<RegId, string>;
export const CONFWORD: Record<Conf, string> = G.legend.confWord as Record<Conf, string>;

export const LAYER: Record<string, string> = G.layers.byEntry;
export const LAYER_ORDER: Record<string, number> = G.layers.order;
export const LAYER_LABEL: Record<string, string> = G.layers.label;
export const LAYER_NOTE: Record<string, string> = G.layers.note;
export const GLOSS: Record<RegId, string> = Object.fromEntries(
  G.regions.map((r) => [r.id, r.gloss ?? '']),
) as Record<RegId, string>;

export const NOTRAIN: Set<string> = new Set(G.legend.noTrain);

// SRC: flatten per-entry sources into the legacy Record<id, [[l, u], ...]> map.
export const SRC: Record<string, [string, string | null][]> = Object.fromEntries(
  G.regions.flatMap((r) => r.entries.map((e) => [e.id, e.sources ?? []] as const)),
);

// ── runtime enrichment ──────────────────────────────────────────────────────

const isTrain = (e: Entry): boolean =>
  e._reg === 'reg-4' ? false : e._reg === 'reg-2' ? !NOTRAIN.has(e.id) : true;

export const byId: Record<string, Entry> = {};

REG.forEach((r, ri) => {
  r.entries.forEach((e) => {
    e._reg = r.id;
    e._ri = ri;
    e._train = isTrain(e);
    e._search = (
      e.nm + ' ' + e.es + ' ' +
      (e.fields || []).map((f) => f.v).join(' ') + ' ' +
      (e.levels || []).map((l) => l.join(' ')).join(' ')
    ).toLowerCase();
    byId[e.id] = e;
  });
});
